// v5
// Path: frontend/src/components/Dashboard.jsx
// Inputs: user, token, onLogout
// Outputs: Master dashboard layout and tool switcher
// Summary: Main administrator portal container with an on-demand (non-persistent) native <dialog> navigation drawer and a compact, mobile/landscape-aware top bar that maximizes tool screen real estate.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DatabaseAdmin } from './DatabaseAdmin.jsx';
import { DatabaseAdminV2 } from './DatabaseAdminV2.jsx';
import { RemoteSQLExplorer } from './RemoteSQLExplorer.jsx';
import { InventoryDeployment } from './InventoryDeployment.jsx';
import SyncHub from './SyncHub/index.jsx';
import DataCanvas from './DataCanvasV3';

export const Dashboard = ({ user, token, onLogout }) => {
    const [view, setView] = useState(null);
    const [tools, setTools] = useState([]);
    const [toolsLoading, setToolsLoading] = useState(true);
    const [toolsError, setToolsError] = useState(null);
    const navDialogRef = useRef(null);
    const navTriggerRef = useRef(null);

    useEffect(() => {
        const fetchTools = async () => {
            setToolsLoading(true);
            setToolsError(null);
            try {
                const res = await fetch('/api/data/tools', { headers: { 'Authorization': `Bearer ${token}` } });
                if (res.status === 401 || res.status === 403) {
                    onLogout();
                    return;
                }
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                let dbTools = await res.json();
                dbTools = dbTools.map(t => (
                    t.RouteIdentifier === 'db-admin'
                        ? { ...t, RouteIdentifier: '/SapphireOps', ToolName: 'SapphireOps (Local)' }
                        : t
                ));
                if (!dbTools.some(t => t.RouteIdentifier === 'quickbooks-sync' || t.RouteIdentifier === 'sync-hub')) {
                    dbTools.push({ ToolName: 'Sync Hub', RouteIdentifier: 'sync-hub', MaterialSymbolName: 'sync_alt' });
                }
                dbTools.push({ ToolName: 'Inventory Deployment', RouteIdentifier: 'inventory-deploy', MaterialSymbolName: 'deployed_code' });
                dbTools.push({ ToolName: 'Database Administration (Deprecated)', RouteIdentifier: 'db-admin-legacy', MaterialSymbolName: 'warning' });
                dbTools.push({ ToolName: 'V3 Data Canvas (Beta)', RouteIdentifier: 'v3-canvas', MaterialSymbolName: 'explore' });
                setTools(dbTools);
            } catch (err) {
                console.error('Failed to load admin tools', err);
                setToolsError(err.message);
            } finally {
                setToolsLoading(false);
            }
        };
        fetchTools();
    }, [token]);

    const openNav = useCallback(() => {
        const dlg = navDialogRef.current;
        if (dlg && !dlg.open) dlg.showModal(); // native focus trap + Esc handling
    }, []);

    const closeNav = useCallback(() => {
        const dlg = navDialogRef.current;
        if (dlg && dlg.open) dlg.close();
    }, []);

    // Return focus to the triggering hamburger whenever the drawer closes (Esc, backdrop, or selection)
    useEffect(() => {
        const dlg = navDialogRef.current;
        if (!dlg) return undefined;
        const handleClose = () => navTriggerRef.current?.focus();
        dlg.addEventListener('close', handleClose);
        return () => dlg.removeEventListener('close', handleClose);
    }, []);

    // Open the drawer automatically on first load when no tool is selected yet
    useEffect(() => {
        if (!view && !toolsLoading && tools.length > 0) openNav();
    }, [toolsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    // Light dismiss: a click landing on the <dialog> element itself (not its panel) is a backdrop click
    const handleDialogClick = (e) => {
        if (e.target === navDialogRef.current) closeNav();
    };

    const selectTool = (routeId) => {
        setView(routeId);
        closeNav();
    };

    const renderTool = () => {
        switch (view) {
            case 'sync-hub':
            case 'quickbooks-sync': // Backward compatibility
                return <SyncHub token={token} />;
            case 'inventory-deploy':
                return <InventoryDeployment token={token} />;
            case '/SapphireOps':
                return <DatabaseAdminV2 token={token} />;
            case 'db-admin-legacy':
                return <DatabaseAdmin token={token} />;
            case '/remote-sql':
                return <RemoteSQLExplorer token={token} />;
            case 'v3-canvas':
                return <DataCanvas defaultViewId={null} token={token} />;
            case null:
                return (
                    <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">
                        <button type="button" onClick={openNav} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-700 hover:bg-slate-800 hover:text-slate-200 focus-visible:ring-2 focus-visible:ring-blue-500/60 outline-none">
                            <span className="material-symbols-outlined text-[18px]">menu</span>
                            Choose a tool
                        </button>
                    </div>
                );
            default:
                return <div className="text-slate-400">Tool module not yet implemented.</div>;
        }
    };

    const currentTool = tools.find(t => t.RouteIdentifier === view);
    const currentToolName = currentTool?.ToolName || 'Command Center';

    return (
        <div className="flex flex-col h-[100dvh] bg-slate-900 text-slate-200 overflow-hidden">
            {/* Compact top bar: 40px normally, 32px on short (landscape phone) viewports */}
            <header className="h-10 [@media(max-height:500px)]:h-8 shrink-0 bg-slate-900 border-b border-slate-800 flex items-center gap-2 px-2 sm:px-3">
                <button
                    ref={navTriggerRef}
                    type="button"
                    onClick={openNav}
                    className="p-1 rounded-md text-slate-300 hover:text-white hover:bg-slate-800 focus-visible:ring-2 focus-visible:ring-blue-500/60 outline-none flex items-center"
                    title="Toggle Navigation Drawer"
                    aria-label="Toggle Navigation Drawer"
                    aria-haspopup="dialog"
                >
                    <span className="material-symbols-outlined text-[22px] [@media(max-height:500px)]:text-[20px]">menu</span>
                </button>
                <span className="text-xs font-bold text-blue-500 tracking-wider hidden sm:inline">SAPPHIRE</span>
                <span className="text-slate-600 hidden sm:inline">/</span>
                {currentTool?.MaterialSymbolName && (
                    <span className="material-symbols-outlined text-[16px] text-slate-400">{currentTool.MaterialSymbolName}</span>
                )}
                <h2 className="text-sm font-medium text-slate-200 truncate">{currentToolName}</h2>
            </header>

            <main className="flex-1 flex flex-col bg-[#0f172a] min-w-0 min-h-0">
                <div className="p-1 sm:p-3 lg:p-5 [@media(max-height:500px)]:p-1 flex-1 overflow-hidden flex flex-col min-w-0 min-h-0">
                    {renderTool()}
                </div>
            </main>

            {/* On-demand navigation drawer (native modal dialog: Esc closes, focus is trapped) */}
            <dialog
                ref={navDialogRef}
                onClick={handleDialogClick}
                aria-label="Sapphire tools"
                className="m-0 p-0 h-[100dvh] max-h-[100dvh] w-72 max-w-[85vw] bg-transparent backdrop:bg-black/60 backdrop:backdrop-blur-sm"
            >
                <nav className="h-full bg-slate-800 border-r border-slate-700 flex flex-col shadow-2xl text-slate-200">
                    <div className="h-12 [@media(max-height:500px)]:h-10 flex items-center justify-between px-4 border-b border-slate-700 bg-slate-900/50 shrink-0">
                        <span className="text-lg font-bold text-blue-500 tracking-wider">SAPPHIRE</span>
                        <button
                            type="button"
                            onClick={closeNav}
                            className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-700 focus-visible:ring-2 focus-visible:ring-blue-500/60 outline-none flex items-center"
                            title="Close menu (Esc)"
                            aria-label="Close menu"
                        >
                            <span className="material-symbols-outlined text-[20px]">close</span>
                        </button>
                    </div>
                    <div className="px-4 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider shrink-0">Admin Tools</div>
                    <div className="flex-1 px-2 space-y-0.5 overflow-y-auto">
                        {toolsLoading && (
                            <div className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400" role="status">
                                <span className="material-symbols-outlined text-[16px] animate-spin">sync</span>
                                Loading tools...
                            </div>
                        )}
                        {!toolsLoading && toolsError && (
                            <div className="px-3 py-2 text-xs text-red-300 bg-red-950/40 border border-red-800/60 rounded-md" role="alert">
                                Failed to load tools ({toolsError}).
                            </div>
                        )}
                        {tools.map((tool) => (
                            <button
                                key={tool.RouteIdentifier}
                                type="button"
                                onClick={() => selectTool(tool.RouteIdentifier)}
                                aria-current={view === tool.RouteIdentifier ? 'page' : undefined}
                                className={`w-full text-left px-3 py-2 [@media(max-height:500px)]:py-1.5 rounded-md text-sm transition-colors flex items-center outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60 ${view === tool.RouteIdentifier ? 'bg-blue-600/20 text-blue-400 font-medium border border-blue-500/20' : 'hover:bg-slate-700/50 text-slate-300 border border-transparent'}`}
                            >
                                <span className="material-symbols-outlined mr-3 text-[20px]">{tool.MaterialSymbolName}</span>
                                {tool.ToolName}
                            </button>
                        ))}
                    </div>
                    <div className="p-3 border-t border-slate-700 bg-slate-800/80 shrink-0">
                        <div className="text-sm font-medium text-slate-300 mb-1 flex items-center">
                            <span className="material-symbols-outlined mr-2 text-[18px]">account_circle</span>
                            {user?.name}
                        </div>
                        <button type="button" onClick={onLogout} className="w-full flex items-center px-3 py-1.5 rounded-md hover:bg-red-500/10 text-red-400 text-sm transition-colors border border-transparent hover:border-red-500/20 outline-none focus-visible:ring-2 focus-visible:ring-red-500/60">
                            <span className="material-symbols-outlined mr-2 text-[18px]">logout</span>
                            Terminate Session
                        </button>
                    </div>
                </nav>
            </dialog>
        </div>
    );
};